import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import {
  clearAssistantConversation,
  getAssistantInFlightRequest,
  getAssistantUsage,
  loadAssistantConversation,
  loadAssistantConversations,
  saveAssistantConversation,
  sendAssistantMessage,
} from "../../actions/mcp-action";
import "./McpPanel.scss";

const MODEL_NOTE = "OpenAI GPT-5 is currently used for this assistant.";
const WELCOME_MESSAGE = {
  id: "welcome",
  role: "assistant",
  text: "Hi, I can help with your StockHero account, bots, deals, balances, and trading questions.",
};

const starterMessages = [
  "How many paper bots do I have?",
  "How is my AAPL bot doing?",
  "How many USD do I have in my paper wallet?",
];

const formatNumber = (value) => Number(value || 0).toLocaleString();

const getUsagePercent = (usage) => {
  if (!usage?.limit) return 0;
  return Math.round((usage.used / usage.limit) * 100);
};

const createConversationId = () =>
  `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getCurrentUserKey = (user) =>
  String(user?.id || user?.email || "anonymous").trim().toLowerCase();

const getConversationStorageKey = (user) =>
  `stockhero-assistant-current-conversation:${getCurrentUserKey(user)}`;

const getPersistableMessages = (items) =>
  items
    .filter((item) => item.id !== WELCOME_MESSAGE.id)
    .filter((item) => item.role === "user" || item.role === "assistant")
    .map((item) => ({
      role: item.role,
      content: String(item.text || item.content || "").trim(),
    }))
    .filter((item) => item.content);

const buildConversationTitle = (items) => {
  const firstUserMessage =
    getPersistableMessages(items).find((item) => item.role === "user")
      ?.content || "StockHero Assistant";
  return firstUserMessage.length > 80
    ? `${firstUserMessage.slice(0, 79)}...`
    : firstUserMessage;
};

const mapSavedMessagesToUi = (items = []) =>
  items
    .filter((item) => item?.role === "user" || item?.role === "assistant")
    .map((item, index) => ({
      id: `saved-${index}-${item.role}`,
      role: item.role,
      text: String(item.content || item.text || "").trim(),
    }))
    .filter((item) => item.text);

const readLocalConversation = (user) => {
  try {
    if (typeof window === "undefined") return null;
    return JSON.parse(
      window.localStorage.getItem(getConversationStorageKey(user)) || "null"
    );
  } catch {
    return null;
  }
};

const writeLocalConversation = (user, conversation) => {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      getConversationStorageKey(user),
      JSON.stringify(conversation)
    );
  } catch {
    // Local persistence is a convenience fallback, not a blocker for chat.
  }
};

const isConversationNotFoundError = (error) =>
  String(error || "").toLowerCase().includes("conversation not found");

const clearLocalConversation = (user) => {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(getConversationStorageKey(user));
  } catch {
    // Local persistence is a convenience fallback, not a blocker for chat.
  }
};

const MessageBubble = ({ message }) => (
  <div className={`assistant-message-row ${message.role}`}>
    <div className="assistant-message-bubble">
      {message.text.split("\n").map((line, index) => (
        <React.Fragment key={`${message.id}-${index}`}>
          {line}
          {index < message.text.split("\n").length - 1 && <br />}
        </React.Fragment>
      ))}
    </div>
  </div>
);

const McpPanel = ({
  authToken,
  clearAssistantConversation,
  currentUser,
  getAssistantUsage,
  loadAssistantConversation,
  loadAssistantConversations,
  saveAssistantConversation,
  sendAssistantMessage,
}) => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [usage, setUsage] = useState({ used: 0, limit: 0, remaining: 0 });
  const [usageError, setUsageError] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [clearingConversation, setClearingConversation] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [thinkingSeconds, setThinkingSeconds] = useState(0);
  const [thinkingStartedAt, setThinkingStartedAt] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const scrollRef = useRef(null);
  const attachedInFlightRequestRef = useRef(null);

  useEffect(() => {
    if (!sending) {
      setThinkingSeconds(0);
      return undefined;
    }

    const startedAt = thinkingStartedAt || Date.now();
    const updateElapsed = () => {
      setThinkingSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    };
    updateElapsed();
    const timer = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(timer);
  }, [sending, thinkingStartedAt]);

  useEffect(() => {
    if (!conversationId || conversationLoading) return undefined;
    const inFlight = getAssistantInFlightRequest(conversationId);
    if (!inFlight?.promise) return undefined;
    if (attachedInFlightRequestRef.current === inFlight.promise) return undefined;

    let cancelled = false;
    attachedInFlightRequestRef.current = inFlight.promise;
    setSending(true);
    setThinkingStartedAt(inFlight.startedAt || Date.now());

    inFlight.promise
      .then((res) => {
        if (cancelled) return;
        const savedConversationId = res.conversationId || conversationId;
        setConversationId(savedConversationId);
        if (res.usage?.limit) {
          setUsage(res.usage);
        }
        setMessages((currentMessages) => {
          const answer = res.answer || "I could not find an answer for that yet.";
          const lastMessage = currentMessages[currentMessages.length - 1];
          if (lastMessage?.role === "assistant" && lastMessage?.text === answer) {
            return currentMessages;
          }
          const nextMessages = [
            ...currentMessages,
            {
              id: `assistant-${Date.now()}`,
              role: "assistant",
              text: answer,
            },
          ];
          const persistedMessages = getPersistableMessages(nextMessages);
          writeLocalConversation(currentUser, {
            conversationId: savedConversationId,
            messages: persistedMessages,
            updatedAt: new Date().toISOString(),
          });
          saveAssistantConversation({
            conversationId: savedConversationId,
            title: buildConversationTitle(nextMessages),
            messages: persistedMessages,
          }).catch(() => {});
          return nextMessages;
        });
      })
      .catch((err) => {
        if (cancelled) return;
        const errorText =
          typeof err === "string"
            ? err
            : "I could not complete that request. Please try again later.";
        setMessages((currentMessages) => [
          ...currentMessages,
          {
            id: `assistant-error-${Date.now()}`,
            role: "assistant",
            text: errorText,
          },
        ]);
      })
      .finally(() => {
        if (!cancelled) {
          setSending(false);
          setThinkingStartedAt(null);
          if (attachedInFlightRequestRef.current === inFlight.promise) {
            attachedInFlightRequestRef.current = null;
          }
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    conversationId,
    conversationLoading,
    currentUser,
    saveAssistantConversation,
  ]);

  useEffect(() => {
    if (!authToken) return;
    setUsageLoading(true);
    setUsageError(false);
    getAssistantUsage()
      .then((nextUsage) => {
        setUsage(nextUsage);
        setUsageError(false);
      })
      .catch(() => {
        setUsage({ used: 0, limit: 0, remaining: 0 });
        setUsageError(true);
      })
      .finally(() => setUsageLoading(false));
  }, [authToken, getAssistantUsage]);

  useEffect(() => {
    if (!authToken) return;

    let cancelled = false;

    const restoreConversation = async () => {
      setConversationLoading(true);
      const localConversation = readLocalConversation(currentUser);

      try {
        let targetConversationId = localConversation?.conversationId || null;
        if (!targetConversationId) {
          const conversations = await loadAssistantConversations();
          targetConversationId = conversations?.[0]?.id || null;
        }

        if (targetConversationId) {
          const savedConversation = await loadAssistantConversation(
            targetConversationId
          );
          const savedMessages = mapSavedMessagesToUi(
            savedConversation?.messages || []
          );
          if (!cancelled && savedMessages.length) {
            setConversationId(savedConversation.id || targetConversationId);
            setMessages([WELCOME_MESSAGE, ...savedMessages]);
            writeLocalConversation(currentUser, {
              conversationId: savedConversation.id || targetConversationId,
              messages: savedMessages,
              updatedAt: savedConversation.updatedAt || new Date().toISOString(),
            });
            return;
          }
        }

        if (!cancelled && localConversation?.messages?.length) {
          setConversationId(localConversation.conversationId || null);
          setMessages([
            WELCOME_MESSAGE,
            ...mapSavedMessagesToUi(localConversation.messages),
          ]);
        }
      } catch (error) {
        if (!cancelled && isConversationNotFoundError(error)) {
          clearLocalConversation(currentUser);
          setConversationId(null);
          setMessages([WELCOME_MESSAGE]);
          return;
        }
        if (!cancelled && localConversation?.messages?.length) {
          setConversationId(localConversation.conversationId || null);
          setMessages([
            WELCOME_MESSAGE,
            ...mapSavedMessagesToUi(localConversation.messages),
          ]);
        }
      } finally {
        if (!cancelled) {
          setConversationLoading(false);
        }
      }
    };

    restoreConversation();

    return () => {
      cancelled = true;
    };
  }, [
    authToken,
    currentUser,
    loadAssistantConversation,
    loadAssistantConversations,
  ]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const submitMessage = (text = message) => {
    const trimmed = text.trim();
    if (!trimmed || sending || conversationLoading) return;
    const nextConversationId = conversationId || createConversationId();

    const userMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text: trimmed,
    };
    const messagesWithUser = [...messages, userMessage];

    setConversationId(nextConversationId);
    setMessages(messagesWithUser);
    setMessage("");
    setSending(true);
    setThinkingStartedAt(Date.now());
    writeLocalConversation(currentUser, {
      conversationId: nextConversationId,
      messages: getPersistableMessages(messagesWithUser),
      updatedAt: new Date().toISOString(),
    });

    const assistantRequest = sendAssistantMessage(
      getPersistableMessages(messagesWithUser),
      nextConversationId
    );
    attachedInFlightRequestRef.current = assistantRequest;
    assistantRequest
      .then((res) => {
        const savedConversationId = res.conversationId || nextConversationId;
        setConversationId(savedConversationId);
        if (res.usage?.limit) {
          setUsage(res.usage);
        }
        getAssistantUsage()
          .then((nextUsage) => {
            setUsage(nextUsage);
            setUsageError(false);
          })
          .catch(() => setUsageError(true));
        const nextMessages = [
          ...messagesWithUser,
          {
            id: `assistant-${Date.now()}`,
            role: "assistant",
            text: res.answer || "I could not find an answer for that yet.",
          },
        ];
        setMessages(nextMessages);
        const persistedMessages = getPersistableMessages(nextMessages);
        writeLocalConversation(currentUser, {
          conversationId: savedConversationId,
          messages: persistedMessages,
          updatedAt: new Date().toISOString(),
        });
        saveAssistantConversation({
          conversationId: savedConversationId,
          title: buildConversationTitle(nextMessages),
          messages: persistedMessages,
        }).catch(() => {});
      })
      .catch((err) => {
        toast.error("Assistant request failed. Please check the message below.");
        const nextMessages = [
          ...messagesWithUser,
          {
            id: `assistant-error-${Date.now()}`,
            role: "assistant",
            text:
              typeof err === "string"
                ? err
                : "I could not complete that request. Please try again later.",
          },
        ];
        setMessages(nextMessages);
        writeLocalConversation(currentUser, {
          conversationId: nextConversationId,
          messages: getPersistableMessages(nextMessages),
          updatedAt: new Date().toISOString(),
        });
      })
      .finally(() => {
        setSending(false);
        setThinkingStartedAt(null);
        if (attachedInFlightRequestRef.current === assistantRequest) {
          attachedInFlightRequestRef.current = null;
        }
      });
  };

  const checkEnterPressed = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (sending) return;
      submitMessage();
    }
  };

  const requestClearConversation = () => {
    if (sending || conversationLoading || clearingConversation) return;
    setShowClearConfirm(true);
  };

  const clearConversation = () => {
    const finishLocalClear = () => {
      clearLocalConversation(currentUser);
      setConversationId(null);
      setMessages([WELCOME_MESSAGE]);
      setShowClearConfirm(false);
    };

    if (!conversationId) {
      finishLocalClear();
      return;
    }

    setClearingConversation(true);
    clearAssistantConversation(conversationId)
      .then(() => {
        finishLocalClear();
        toast.success("Conversation cleared.");
      })
      .catch((err) => {
        if (isConversationNotFoundError(err)) {
          finishLocalClear();
          toast.success("Conversation cleared.");
          return;
        }
        toast.error(
          typeof err === "string" ? err : "Unable to clear this conversation."
        );
      })
      .finally(() => setClearingConversation(false));
  };

  const usagePercent = getUsagePercent(usage);
  const usageBarPercent = Math.min(100, usagePercent);
  const usageExceeded = Boolean(usage.limit && usage.used >= usage.limit);
  const usagePercentLabel = usageLoading
    ? "Loading"
    : usage.limit
      ? `${usagePercent}%`
      : "-";
  const usageLabel = usageLoading
    ? "Checking your daily limit..."
    : usage.limit
    ? `${formatNumber(usage.used)} / ${formatNumber(usage.limit)} tokens`
    : usageError
      ? "Usage unavailable"
      : "Daily limit unavailable";

  return (
    <div className="setting-general-content assistant-panel">
      <div className="assistant-panel-header">
        <div>
          <h5>StockHero Assistant</h5>
          <p>{MODEL_NOTE}</p>
        </div>
        <div className={`assistant-usage-card ${usageExceeded ? "is-over-limit" : ""}`}>
          <div className="assistant-usage-topline">
            <span>Daily usage</span>
            <strong>{usagePercentLabel}</strong>
          </div>
          <div className="assistant-usage-track">
            <div
              className="assistant-usage-bar"
              style={{ width: `${usageBarPercent}%` }}
            />
          </div>
          <small>{usageLabel}</small>
          {usageExceeded && !usageLoading && (
            <small className="assistant-usage-warning">Daily limit exceeded. New assistant requests may be blocked.</small>
          )}
        </div>
      </div>

      <div className="assistant-chat-shell">
        <div className="assistant-chat-toolbar">
          <button
            type="button"
            className="assistant-clear-button"
            onClick={requestClearConversation}
            disabled={sending || conversationLoading || clearingConversation || messages.length <= 1}
          >
            {clearingConversation ? "Clearing..." : "Clear conversation"}
          </button>
        </div>

        <div className="assistant-chat-history" ref={scrollRef}>
          {conversationLoading && (
            <div className="assistant-message-row assistant">
              <div className="assistant-message-bubble assistant-typing" aria-live="polite">
                <span>Loading conversation</span>
                <span className="assistant-typing-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            </div>
          )}
          {messages.map((item) => (
            <MessageBubble key={item.id} message={item} />
          ))}
          {sending && (
            <div className="assistant-message-row assistant">
              <div className="assistant-message-bubble assistant-typing" aria-live="polite">
                <span>Thinking</span>
                <span className="assistant-typing-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
                <span className="assistant-thinking-time">{thinkingSeconds}s</span>
              </div>
            </div>
          )}
        </div>

        <div className="assistant-starters">
          {starterMessages.map((starter) => (
            <button
              key={starter}
              type="button"
              onClick={() => submitMessage(starter)}
              disabled={sending || conversationLoading}
            >
              {starter}
            </button>
          ))}
        </div>

        <div className="assistant-composer">
          <Form.Control
            as="textarea"
            rows={2}
            value={message}
            placeholder="Ask about your bots, deals, balances, or StockHero features..."
            onChange={(event) => setMessage(event.target.value)}
            onKeyDown={checkEnterPressed}
            disabled={conversationLoading}
          />
          <Button
            variant=""
            className="assistant-send-button"
            onClick={() => submitMessage()}
            disabled={sending || conversationLoading || !message.trim()}
          >
            {sending ? "Sending" : "Send"}
          </Button>
        </div>
      </div>

      <Modal
        show={showClearConfirm}
        onHide={() => !clearingConversation && setShowClearConfirm(false)}
        centered
        className="assistant-clear-modal"
      >
        <Modal.Header closeButton={!clearingConversation}>
          <Modal.Title>Clear conversation?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Clear the current assistant chat?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant=""
            className="assistant-modal-cancel"
            onClick={() => setShowClearConfirm(false)}
            disabled={clearingConversation}
          >
            Cancel
          </Button>
          <Button
            variant=""
            className="assistant-modal-confirm"
            onClick={clearConversation}
            disabled={clearingConversation}
          >
            {clearingConversation ? "Clearing..." : "Clear chat"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  authToken: state.auth.token,
  currentUser: state.auth.user,
});

export default connect(mapStateToProps, {
  getAssistantUsage,
  clearAssistantConversation,
  loadAssistantConversation,
  loadAssistantConversations,
  saveAssistantConversation,
  sendAssistantMessage,
})(McpPanel);
